import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, errorText } from "../../api";
import { canShowPdf, PdfPages } from "../../components/Pdf";
import { fmtDate, fmtDateTime, monthName } from "../../lib/format";
import { useToast } from "../../lib/toast";
import { useApi } from "../../lib/useApi";
import { Aufteilen } from "./Aufteilen";
import type { Category, KanzleiBeleg } from "./types";

export const CATEGORY_LABEL: Record<Category, string> = {
  ausgang: "Ausgangsbelege",
  eingang: "Eingangsbelege",
  kontoauszug: "Kontoauszug",
};
const IMAGE = /^image\/(jpeg|png|gif|webp)$/;
// Kontoauszug-Datei aus dem Online-Banking (CAMT als XML, MT940 als Text, CSV, ZIP): gibt es nur im Ordner „Kontoauszug“;
// eine XML-Datei dort ist keine E-Rechnung
const BANK_MIME = /^(application\/(xml|zip)|text\/(plain|csv))$/;
export const istBankDatei = (b: Pick<KanzleiBeleg, "category" | "mime">) =>
  b.category === "kontoauszug" && BANK_MIME.test(b.mime);

// Beleg-Fenster der Kanzlei: Vorschau, Herunterladen, Kommentare an den Mandanten und (PDF, nicht gebucht) Seiten
// aufteilen. Öffnen markiert den Beleg für mich als gesehen.
export function BelegDialog({ id, onClose }: { id: string; onClose(): void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const res = useApi<KanzleiBeleg>(`/api/kanzlei/beleg/${id}`, {
    live: ["belege"],
  });
  const b = res.data;
  const [teilen, setTeilen] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    api.post(`/api/kanzlei/beleg/${id}/seen`).catch(() => {});
  }, [id]);

  const fileUrl = `/api/kanzlei/beleg/${id}/file`;
  return (
    <dialog
      ref={ref}
      id="lview"
      className="blg"
      aria-label={b ? `Beleg ${b.name}` : "Beleg"}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="lv-h">
        <b>
          {b
            ? `${CATEGORY_LABEL[b.category]} · ${monthName(b.month)} ${b.year}`
            : "Beleg"}
        </b>
        <span className="note">
          {b
            ? `${b.name} · ${b.clientName}${b.changeNote ? ` · ${b.changeNote}` : ""} · Beleg-Nr. ${b.nr} · ${
                b.bookedAt
                  ? `gebucht am ${fmtDate(b.bookedAt)}`
                  : "nicht gebucht"
              }`
            : res.error}
        </span>
        {b && b.mime === "application/pdf" && !b.bookedAt && !teilen && (
          <button
            type="button"
            className="btn sm"
            onClick={() => setTeilen(true)}
          >
            Seiten aufteilen
          </button>
        )}
        {b && (
          <a className="btn sm" href={`${fileUrl}?download=1`} download>
            Herunterladen
          </a>
        )}
        <button
          type="button"
          className="btn sm dark"
          onClick={onClose}
          autoFocus
        >
          Schließen
        </button>
      </div>
      {b && (
        <>
          {IMAGE.test(b.mime) ? (
            <div className="lv-b">
              <img src={fileUrl} alt={`Beleg ${b.name}`} />
            </div>
          ) : b.mime === "application/pdf" && canShowPdf() ? (
            <object
              data={fileUrl}
              type="application/pdf"
              className="lv-b"
              aria-label={b.name}
            >
              <p className="note lv-np">
                Die PDF kann hier nicht angezeigt werden. Bitte herunterladen.
              </p>
            </object>
          ) : b.mime === "application/pdf" ? (
            <div className="lv-b">
              <PdfPages
                url={fileUrl}
                title={b.name}
                fallback={
                  <p className="note lv-np">
                    Die PDF kann hier nicht angezeigt werden. Bitte
                    herunterladen.
                  </p>
                }
              />
            </div>
          ) : istBankDatei(b) ? (
            <div className="lv-b">
              <p className="note lv-np">
                Bankdatei (Kontoauszug). Keine Vorschau, die Seite „Buchen“
                liest die Datei ein.
              </p>
            </div>
          ) : b.mime === "application/xml" ? (
            <div className="lv-b">
              <p className="note lv-np">
                E-Rechnung (XML). Bitte herunterladen und in die Buchhaltung
                einlesen.
              </p>
            </div>
          ) : (
            <div className="lv-b">
              <p className="note lv-np">
                Vorschau nicht verfügbar. Bitte herunterladen.
              </p>
            </div>
          )}
          {teilen ? (
            <div className="lv-c auf-c">
              <Aufteilen
                b={b}
                onDone={onClose}
                onCancel={() => setTeilen(false)}
              />
            </div>
          ) : (
            <div className="lv-c">
              <div className="note" style={{ marginBottom: 6 }}>
                <b>Kommentare zum Beleg</b> · sieht {b.contactName} in der App
              </div>
              <div className="thread">
                {b.comments.map((c) => (
                  <div className="cm" key={c.id}>
                    <b>{c.authorName}</b>
                    {c.side === "client" && (
                      <>
                        {" "}
                        <span className="pill p-info">Mandant</span>
                      </>
                    )}{" "}
                    <span className="note">{fmtDateTime(c.createdAt)}</span>
                    <div>{c.body}</div>
                  </div>
                ))}
                <CommentForm b={b} reload={res.reload} />
              </div>
            </div>
          )}
        </>
      )}
    </dialog>
  );
}

function CommentForm({
  b,
  reload,
}: {
  b: KanzleiBeleg;
  reload(): Promise<void>;
}) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  // Fehler stehen direkt am Eingabefeld
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/kanzlei/beleg/${b.id}/comments`, { body });
      setText("");
      await reload();
      toast(`Kommentar gesendet · ${b.contactName} sieht ihn in der App`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {error && (
        <p className="note lv-err" role="alert">
          {error}
        </p>
      )}
      <form className="cform" onSubmit={submit}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          placeholder={`Kommentar an ${b.contactName}, z. B. Bitte Rechnung mit vollem Namen nachreichen`}
          aria-label="Kommentar zum Beleg"
        />
        <button type="submit" className="btn sm" disabled={busy}>
          Senden
        </button>
      </form>
    </>
  );
}
